import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { LinkButton } from 'ui/Buttons';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { ActionsMenu, ChipButton, DataTable, Link } from '@scope/scope-one-ui/core';
import {
  ColumnDefinition,
  RowData,
  TableActions,
} from '@scope/scope-one-ui/core/DataTable/DataTable.d';
import EyeIcon from '@scope/scope-one-ui/assets/icons/eye.svg';
import ContractIcon from '@scope/scope-one-ui/assets/icons/file.svg';
import LockIcon from '@scope/scope-one-ui/assets/icons/lock.svg';
import { getRatings, deactivateRatings, getRating } from './services/rsrService';
import { Rating, SubscriptionRatingType } from './types';
import { SetContractTypeModal } from './Modals/SetContractTypeModal';
import { useRSR } from '../../../shared/hooks';
import { ConfirmModal } from './Modals/ConfirmModal';

type RatingsListProps = {
  ratingType: SubscriptionRatingType;
};

export const RatingsList: FC<RatingsListProps> = ({ ratingType }) => {
  const { t } = useTranslation();
  const { error, success } = useToasts();
  const [selectedRating, setSelectedRating] = useState<Rating>();
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // RENDERERS
  const nameRenderer = ({ entityId, entityType, entityName }: Rating) => (
    <LinkButton
      to={`/${{ ISSUE: 'instruments', ISSUER: 'entities' }[entityType]}/${entityId}?module=credit`}
      label={entityName?.toUpperCase()}
      color="primary"
    />
  );

  const contractTypeRenderer = (contractType: string) =>
    contractType ? t(`ADMIN.RSR.${contractType}`) : '-';

  const entityTypeRenderer = (entityType: string) =>
    entityType ? t(`ADMIN.RSR.ENTITY_TYPE_${entityType}`) : '-';

  // ACTIONS
  const getActions = (item: Rating) => [
    {
      label: t('ADMIN.RSR.VIEW_DETAILS'),
      icon: <EyeIcon />,
      onClick: () => showRatingDetails(item),
    },
    {
      label: t('ADMIN.RSR.SET_CONTRACT_TYPE'),
      icon: <ContractIcon />,

      onClick: () => showContractTypeModal(item),
    },
    {
      label: t('ADMIN.RSR.REVOKE_ALL_USERS'),
      onClick: () => {
        setSelectedIds([item.entityId]);
        setIsConfirmModalOpen(true);
      },
      icon: <LockIcon />,
    },
  ];

  // COLUMN DEFINITION
  const initialColumns: ColumnDefinition[] = [
    {
      name: 'entityName',
      label: t('ADMIN.RSR.RSR_NAME'),
      customBodyRender: nameRenderer,
      sortable: true,
      sortComparator: (a, b) => (a.name > b.name ? 1 : -1),
      searchable: true,
      getSearchValue: (v) => v.entityName,
    },
    {
      name: 'entityType',
      label: t('ADMIN.RSR.TYPE'),
      sortable: true,
      customBodyRender: entityTypeRenderer,
    },
    {
      name: 'ratingType',
      label: t('ADMIN.RSR.RATING_TYPE'),
      sortable: true,
    },
    {
      name: 'contractType',
      label: t('ADMIN.RSR.CONTRACT_TYPE'),
      customBodyRender: contractTypeRenderer,
      sortable: true,
      sortComparator: (a, b) => (a.name > b.name ? 1 : -1),
    },
    {
      name: 'count',
      label: t('ADMIN.RSR.NO_OF_REGISTERED_USERS'),
      sortable: true,
      customBodyRender: ({ numberOfUsers, ratingId, entityName, ratingType: entityRatingType }) => (
        <Link
          href={`/ca/admin-panel/${ratingType}/ratings/${ratingId}?name=${encodeURIComponent(
            entityName,
          )}${entityRatingType ? `&ratingType=${encodeURIComponent(entityRatingType)}` : ''}`}
        >
          <ChipButton>{numberOfUsers}</ChipButton>
        </Link>
      ),
    },
    {
      name: 'moreActions',
      label: ' ',
      customBodyRender: (v) => <ActionsMenu list={getActions(v)} />,
    },
  ];
  const {
    tableActions,
    selectToggle,
    selectedIds,
    searchedValue,
    sort,
    rows,
    loading,
    setLoading,
    pagination,
    setRows,
    isSelectedAll,
    selectAllToggle,
    setTotal,
    setSelectedIds,
    clearSelectedIds,
  } = useRSR({ initialColumns, defaultSortField: 'entityName' });

  const buildRows = (data: Rating[]) =>
    data.map((entity) => {
      const {
        ratingId,
        entityId,
        entityName,
        entityType,
        ratingType: entityRatingType,
        contractType,
      } = entity;

      return {
        id: ratingId,
        data: {
          entityName: { entityId, entityName, entityType },
          entityType,
          ratingType: entityRatingType,
          contractType,
          count: entity,
          moreActions: entity,
        },
      };
    });

  const fetchRatings = () => {
    setLoading(true);
    getRatings(
      ratingType,
      pagination.page,
      pagination.limit,
      {
        sort: sort.field,
        dir: sort.order,
      },
      searchedValue,
    )
      .then((response) => {
        setRows(buildRows(response.data));
        setTotal(response.total);
      })
      .catch((er) => {
        error({ title: '', content: 'Error when loading ratings' });
      })
      .finally(() => setLoading(false));
  };

  const fetchDebounce = useDebouncedCallback(fetchRatings, 300);

  useEffect(() => {
    fetchDebounce();
  }, [pagination, sort, searchedValue]);

  const deactivateAllUsers = async (ids: string[]) => {
    try {
      await deactivateRatings(ratingType, ids);
      success({ title: '', content: t('ADMIN.SNACKS.REVOKED_ALL_RATING_USERS') });
      fetchRatings();
    } catch (e) {
      error({ title: '', content: t('ADMIN.SNACKS.REVOKE_ALL_RATING_USERS_FAILED') });
    }
  };

  const showRatingDetails = (item: Rating) => {
    const urls = {
      ISSUE: '/ca/instruments/',
      ISSUER: '/ca/entities/',
    } as { [key: string]: string };
    const id = item?.entityId;
    const type = item?.entityType;

    if (id && type) {
      window.location.href = `${urls[type]}${id}?module=credit`;
    }
  };

  const fetchRating = async (ratingId) =>
    getRating(ratingType, ratingId).then((resp) => {
      setSelectedRating(resp);
    });

  const showContractTypeModal = async (item: Rating) => {
    await fetchRating(item.ratingId);
    setIsContractModalOpen(true);
  };

  const handleContractTypeModalClose = (isRefreshRequired?: boolean) => {
    setIsContractModalOpen(false);
    if (isRefreshRequired) {
      fetchRatings();
    }
  };

  const headerActions = [
    {
      icon: <LockIcon />,
      label: 'REVOKE ALL USERS',
      onClick: () => {
        setIsConfirmModalOpen(true);
      },
    },
  ];

  return (
    <>
      <DataTable
        selection={{
          selectedIds,
          selectToggle,
          isSelectedAll,
          selectAllToggle,
        }}
        headerActions={headerActions}
        loading={loading}
        title="Ratings"
        cols={initialColumns}
        rows={rows}
        selectable
        paginate
        tableActions={tableActions}
      />
      <SetContractTypeModal
        open={isContractModalOpen}
        onClose={handleContractTypeModalClose}
        rating={selectedRating}
        ratingType={ratingType}
      />
      <ConfirmModal
        variant="warning"
        title="Revoke all"
        text="Do you really want to revoke access for all users?"
        confirmLabel="Revoke all"
        onClose={(v) => {
          setSelectedIds([]);
          setIsConfirmModalOpen(false);
        }}
        onConfirm={() => {
          if (setSelectedIds.length) {
            deactivateAllUsers(selectedIds);
            setSelectedIds([]);
            setIsConfirmModalOpen(false);
            fetchRatings();
          }
        }}
        open={isConfirmModalOpen}
      />
    </>
  );
};
